import { useEffect, useRef } from 'react';
import { useAuthContext, useCurrentUser } from '@tyndo/hooks';
import { recordError, recordEvent } from '../observability/telemetry';

/**
 * Proactively refreshes the access token before it expires.
 *
 * `AuthProvider.refreshToken()` exists but had zero callers, so the only thing
 * that ever happened on token expiry was a hard logout (the `auth:unauthorized`
 * handler). This hook schedules a refresh a short safety margin before the
 * decoded JWT `exp`, so a long-running operator session is kept alive silently
 * instead of being booted to /login mid-task.
 *
 * Dev tokens (`dev-token-*`) decode to a synthetic 24h profile and don't have a
 * real refresh endpoint, so they are skipped.
 *
 * NOTE: the reactive 401 refresh-then-retry path lives in the shared API client
 * (`packages/hooks/useApiClient.ts`); this hook is the proactive complement.
 */

// Refresh this many ms before the token's `exp` so a slow refresh round-trip
// still completes before the old token is rejected.
const REFRESH_MARGIN_MS = 60_000;
// Never schedule sooner than this to avoid a tight refresh loop on a token
// that is already near/at expiry.
const MIN_DELAY_MS = 5_000;

export function useProactiveTokenRefresh(): void {
  const { token, refreshToken } = useAuthContext();
  const user = useCurrentUser();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!token || token.startsWith('dev-token-') || !user?.exp) {
      return;
    }

    const expiresAtMs = user.exp * 1000;
    const delay = Math.max(expiresAtMs - Date.now() - REFRESH_MARGIN_MS, MIN_DELAY_MS);

    timerRef.current = setTimeout(() => {
      void refreshToken()
        .then(() => recordEvent('auth.token_refreshed', 'info', {}))
        .catch((err) => {
          // refreshToken() already logs out on failure; we just record it.
          recordError('auth.token_refresh_failed', err, {});
        });
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [token, user?.exp, refreshToken]);
}
